import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { Constants } from 'app/shared/shared-constants';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { RestResourceCopySource } from 'app/model/pipeline/copysource/rest-source.copysource';
import { compressionCodecType, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { documentFormOptions } from 'app/authoring/component/transformations/complex-type-utils';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';

export const restSinkRequestMethods = [
    {
        label: stringResources.Dataset_RequestMethod_Put,
        value: 'PUT'
    },
    {
        label: stringResources.Method_Post,
        value: 'POST'
    },
    {
        label: stringResources.Dataset_RequestMethod_Patch,
        value: 'PATCH'
    }
];

const restV1SourceControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'httpRequestTimeout',
            label: stringResources.ModelName_Request_Timeout,
            validators: [
                {
                    type: 'pattern',
                    pattern: Constants.timeoutValidationPattern,
                    errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_Request_Timeout)
                }
            ],
            description: stringResources.Dataset_HttpRequestTimeoutDescription
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox-number',
            bind: 'requestInterval',
            label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
            description: stringResources.REST_CopySource_Request_Interval_Description,
            min: 10,
            max: 60000
        },
        dynamicContentTextClass: 'input-spaced'
    }
];

export const restResourceMetadata: IDatasetTypeMetadata = {
    type: Metamodel.modelFor(RestResourceDataset).name,
    displayName: stringResources.RestService_DisplayText,
    supportLinkedServiceTypes: [Metamodel.modelFor(RestServiceLinkedService).name],
    isSource: true,
    isSink: true,
    isTableNameRequired: true,
    supportDataFlowWriteComplexStructure: true,
    datasetKind: 'hierarchical',
    notSupportTypeConversion: true,
    notSupportPhysicalType: true,
    supportCreatedAsV2: true,
    supportDataFlowSink: true,
    supportDataFlowSource: true,
    icon: {
        className: 'svg-i-100x100-REST'
    },
    isFirstClass: true,
    category: ['GenericProtocol'],
    notSupportLookupActivity: true,
    copySourceControls: restV1SourceControls,
    v2CopySourceControls: [
        {
            type: 'rest-request-body',
            bind: {
                requestMethod: 'requestMethod',
                requestBody: 'requestBody'
            }
        },
        ...restV1SourceControls,
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string, owner: Owned) => {
                if (!!item) {
                    if (item.toLowerCase() === 'accept') {
                        return stringResources.Rest_Additional_Headers_Warning;
                    }
                    if (item.toLowerCase() === 'content-type' && owner instanceof RestResourceCopySource &&
                        stringIsNullOrEmpty(owner.requestBody)
                        && !RestResourceCopySource.BODY.templateValue(owner)) {
                        return stringResources.Rest_ContentType_Header_Warning;
                    }
                }
                return null;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'warning',
            isLayoutVertical: false
        },
        {
            type: 'rest-pagination-rules',
            bind: 'paginationRules',
            label: stringResources.Dataset_PaginationRules,
            description: stringResources.Dataset_PaginationRulesDescription,
            descriptionLink: 'https://go.microsoft.com/fwlink/?linkid=2148868',
            isLayoutVertical: false
        }
    ],
    copySinkControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'requestMethod',
                label: stringResources.Dataset_RequestMethod,
                outsideAdvancedSectionInFabric: true,
                options: restSinkRequestMethods,
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'httpRequestTimeout',
                label: stringResources.ModelName_Request_Timeout,
                validators: [
                    {
                        type: 'pattern',
                        pattern: Constants.timeoutValidationPattern,
                        errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_Request_Timeout)
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySink_Request_Interval_Description,
                min: 10,
                max: 60000
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'writeBatchSize',
                label: stringResources.ModelName_WriteBatchSize,
                description: stringResources.ModelName_REST_Sink_Write_Batch_Size
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'compressionType',
                options: [
                    {
                        label: stringResources.None,
                        value: compressionCodecType.none
                    },
                    {
                        label: stringResources.Dataset_CompressionType_Option_GZip,
                        value: compressionCodecType.gzip
                    }
                ],
                defaultValue: compressionCodecType.none,
                label: stringResources.ModelName_HttpCompressionType,
                description: stringResources.ModelName_HttpCompressionType
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            parameterizable: true,
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string) => {
                return (!!item && (item.toLowerCase() === 'accept-encoding' || item.toLowerCase() === 'content-encoding')) ?
                    stringResources.Rest_Sink_Additional_Headers_Warning : undefined;
            },
            hintMode: 'warning'
        }
    ],
    dataflowSourceControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'requestMethod',
                label: stringResources.Dataset_RequestMethod,
                options: [
                    {
                        label: stringResources.Dataset_RequestMethod_Get,
                        value: 'GET'
                    },
                    {
                        label: stringResources.Method_Post,
                        value: 'POST'
                    }
                ],
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                defaultValue: 'GET',
                description: stringResources.Dataset_RequestMethodDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textarea',
                bind: 'requestBody',
                label: stringResources.Dataset_RequestBody,
                description: stringResources.Dataset_RequestBodyDescription,
                isDataFlowExpression: true
            },
            hide: `{ requestMethod.expression === 'GET' }`,
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestTimeout',
                label: `${stringResources.ModelName_Request_Timeout} (s)`,
                description: stringResources.DataFlow_HttpRequestTimeoutDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySource_Request_Interval_Description,
                min: 0,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string, owner: Owned) => {
                if (!!item) {
                    if (item.toLowerCase() === 'accept') {
                        return stringResources.DataFlow_Rest_Additional_Headers_Warning;
                    }
                    if (item.toLowerCase() === 'content-type' && owner instanceof DataFlowSource
                        && owner.requestBody.expression) {
                        return stringResources.Rest_ContentType_Header_Warning;
                    }
                }
                return null;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'info',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'queryParameters',
            label: stringResources.Query_Parameters,
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'rest-pagination-rules',
            bind: 'paginationRules',
            label: stringResources.Dataset_PaginationRules,
            description: stringResources.Dataset_PaginationRulesDescription,
            descriptionLink: 'https://go.microsoft.com/fwlink/?linkid=2148868',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.ModelName_XmlDataset,
                    value: 'xml'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'parameterizable',
                                    child: {
                                        type: 'radio',
                                        label: stringResources.DataFlowSourceSettingsDocumentFormLabel,
                                        description: stringResources.DataFlowSourceSettingsDocumentFormDescription,
                                        options: documentFormOptions,
                                        bind: 'documentForm',
                                        isDataFlowExpression: true
                                    },
                                    dynamicContentTextClass: 'input-spaced',
                                    showErrorIcon: false,
                                    isDataFlowExpression: true
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: 'xml',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsXmlAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'textbox',
                                    label: stringResources.NullValueFieldLabel,
                                    bind: 'nullValue'
                                },
                                {
                                    type: 'xml-namespace-prefix',
                                    bind: {
                                        namespacePrefixes: 'namespacePrefixes',
                                        namespaces: 'namespaces'
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        }
    ],
    dataflowSinkControls: [
        {
            type: 'rest-update-settings',
            bind: {
                allowInsert: 'allowInsert',
                allowDelete: 'allowDelete',
                allowUpsert: 'allowUpsert',
                allowUpdate: 'allowUpdate',
                insertHttpMethod: 'insertHttpMethod',
                deleteHttpMethod: 'deleteHttpMethod',
                upsertHttpMethod: 'upsertHttpMethod',
                updateHttpMethod: 'updateHttpMethod',
                rowRelativeUrl: 'rowRelativeUrl',
                skipRowRelativeUrl: 'skipRowRelativeUrl'
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'httpCompressionType',
                label: stringResources.ModelName_HttpCompressionType,
                options: [
                    {
                        label: stringResources.None,
                        value: undefined
                    },
                    {
                        label: stringResources.Dataset_CompressionType_Option_GZip,
                        value: compressionCodecType.gzip
                    }
                ],
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'batchSize',
                label: stringResources.ModelName_BatchSize,
                description: stringResources.DataFlow_BatchSize_Description,
                isDataFlowExpression: true
            },
            hide: `{ !restDataFormat }`,
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestTimeout',
                label: `${stringResources.ModelName_Request_Timeout} (s)`,
                description: stringResources.DataFlow_HttpRequestTimeoutDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySource_Request_Interval_Description,
                min: 0,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string) => {
                return (!!item && item.toLowerCase() === 'content-encoding') ?
                    stringResources.DataFlow_Rest_Sink_Additional_Headers_Encoding_Warning :
                    (!!item && item.toLowerCase() === 'content-type') ? stringResources.DataFlow_Rest_Sink_Additional_Headers_Content_Warning : undefined;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'info',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'queryParameters',
            label: stringResources.Query_Parameters,
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'select',
                                    label: stringResources.DateFormat_Label,
                                    options: DataFlowTypesUtils.commonFormats('date').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'dateFormat'
                                },
                                {
                                    type: 'select',
                                    label: stringResources.Timestamp_Format_Label,
                                    options: DataFlowTypesUtils.commonFormats('timestamp').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'timestampFormat'
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        }
    ]
};
